import { DirectoryItem } from './types'
import { computed, Ref, ref, watch } from 'vue'
import { getDirectoryItemBlob } from './files.service'
import { useI18n } from 'vue-i18n'
import { AxiosError } from 'axios'

export type PreviewType = 'image' | 'pdf' | 'text' | 'markdown' | 'video' | 'none'

function readFileAsText(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = () => resolve(reader.result as string)
    reader.onerror = reject

    reader.readAsText(file)
  })
}

export function useFilePreview(previewFile: Ref<DirectoryItem<'file'> | undefined>) {
  const blob = ref<Blob>()
  const textContent = ref<string>()
  const isLoading = ref(false)
  const blobUrl = ref<string>()
  const errorMessage = ref<string>()

  const { t } = useI18n()

  const previewType = computed<PreviewType>(() => {
    if (previewFile.value && previewFile.value.type === 'file') {
      const ext = previewFile.value.name.split('.').pop()?.toLowerCase() ?? ''
      if (['jpg', 'jpeg', 'png', 'gif', 'svg'].includes(ext)) return 'image'
      if (['pdf'].includes(ext)) return 'pdf'
      if (['txt'].includes(ext)) return 'text'
      if (['md', 'markdown'].includes(ext)) return 'markdown'
      if (['mp4', 'webm', 'ogg'].includes(ext)) return 'video'
    }
    return 'none'
  })

  function transformBlob(blob: Blob): Blob {
    const ext = previewFile.value?.name.split('.').pop()?.toLowerCase() ?? ''
    if (ext === 'svg' && blob.type !== 'image/svg+xml') {
      return new Blob([blob], { type: 'image/svg+xml' })
    } else if (ext === 'txt' || ext === 'md' || ext === 'markdown') {
      return new Blob([blob], { type: 'text/plain' })
    }
    return blob
  }

  function handleFileErrorText(error: AxiosError, isPdf: boolean = false): string {
    if (error?.response?.status === 404) {
      return t('general__data__file-not-found')
    } else if (error && isPdf) {
      return t('general__text__preview-pdf-error')
    } else return t('general__text__preview-not-available-for-file-type')
  }

  watch(
    previewFile,
    async (newFile) => {
      if (newFile) {
        isLoading.value = true
        try {
          const newBlob = await getDirectoryItemBlob(newFile)
          if (newBlob) {
            blob.value = transformBlob(newBlob)
          }
        } catch (e) {
          console.error('Error fetching file blob:', e)
          errorMessage.value = handleFileErrorText(e as AxiosError, previewType.value === 'pdf')
          blob.value = undefined
        } finally {
          isLoading.value = false
        }
      }
    },
    { immediate: true }
  )

  watch(
    blob,
    async (newBlob) => {
      if (newBlob) {
        if (blobUrl.value) {
          URL.revokeObjectURL(blobUrl.value)
        }
        blobUrl.value = URL.createObjectURL(newBlob)
        if (newBlob.type.startsWith('text/')) {
          textContent.value = await readFileAsText(newBlob)
        }
      }
    },
    { immediate: true }
  )

  function setPreviewFile(file: DirectoryItem<'file'>) {
    previewFile.value = file
  }

  function revokeBlobUrl() {
    if (blobUrl.value) {
      URL.revokeObjectURL(blobUrl.value)
      blobUrl.value = undefined
    }
  }

  function clearPreviewFile() {
    blobUrl.value = undefined
    blob.value = undefined
    blobUrl.value = undefined
    revokeBlobUrl()
  }

  return {
    previewType,
    previewFile,
    blob,
    blobUrl,
    textContent,
    isLoading,
    errorMessage,
    revokeBlobUrl,
    setPreviewFile,
    clearPreviewFile
  }
}
